'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from 'react';
import { isEmbeddableMediaUrl } from '@/lib/contentLinks';
import Image from 'next/image';
import Link from 'next/link';
import { toDescriptionParagraphs, isSoldStatus, formatListingDate, type MLSProperty } from '@/lib/listings';
import PropertyMap from '@/components/PropertyMap';
import SavePropertyButton from '@/components/SavePropertyButton';
import { getUTMData } from './UTMCapture';
import { getRecaptchaToken } from '@/lib/recaptchaClient';
import { trackLeadSubmitted } from '@/lib/tracking';
import { formatPhone, phoneHref } from '@/lib/phoneUtils';
interface ListingAgentInfo {
  name: string;
  slug: {
    current: string;
  };
  title?: string;
  imageUrl?: string | null;
  email?: string;
  phone?: string;
  mobile?: string;
}
interface RCSothebysListingContentProps {
  listing: MLSProperty;
  agent: ListingAgentInfo | null;
  coAgent?: ListingAgentInfo | null;
  /** Brokerage contact shown on non-exclusive listings (e.g. Chris Klug). */
  defaultAgent?: ListingAgentInfo | null;
  googleMapsApiKey?: string;
}
function formatPrice(price: number | null): string {
  if (!price) return 'Price Upon Request';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}
export default function KlugListingContent({
  listing,
  agent,
  coAgent,
  defaultAgent,
  googleMapsApiKey
}: RCSothebysListingContentProps) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [failedPhotos, setFailedPhotos] = useState<Set<number>>(new Set());

  // Sold listings lead with what the property actually closed at, not the
  // asking price. transformListing normalizes 'Closed' to 'Sold', so match both.
  const isSold = isSoldStatus(listing.status);
  const soldDate = isSold ? formatListingDate(listing.sold_date) : null;
  const displayPrice = isSold ? formatPrice(listing.sold_price ?? listing.list_price) : formatPrice(listing.list_price);

  // Contact form state
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  // Bottom contact form state
  const [btmFirstName, setBtmFirstName] = useState('');
  const [btmLastName, setBtmLastName] = useState('');
  const [btmEmail, setBtmEmail] = useState('');
  const [btmPhone, setBtmPhone] = useState('');
  const [btmMessage, setBtmMessage] = useState('');
  const [btmSubmitting, setBtmSubmitting] = useState(false);
  const [btmSubmitted, setBtmSubmitted] = useState(false);
  const [btmError, setBtmError] = useState('');
  const handleBtmFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!btmFirstName || !btmLastName || !btmEmail) return;
    setBtmSubmitting(true);
    setBtmError('');
    try {
      const utm = getUTMData();
      const recaptchaToken = await getRecaptchaToken('submit_lead');
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recaptchaToken,
          firstName: btmFirstName,
          lastName: btmLastName,
          email: btmEmail,
          phone: btmPhone || undefined,
          message: btmMessage || undefined,
          leadType: 'property_inquiry',
          propertyAddress: listing.address || `Property ${listing.mls_number}`,
          propertyMlsId: listing.mls_number || undefined,
          propertyPrice: listing.list_price || undefined,
          source: 'Property Detail Page - Bottom Form',
          sourceUrl: utm.source_url,
          referrer: utm.referrer,
          utmSource: utm.utm_source,
          utmMedium: utm.utm_medium,
          utmCampaign: utm.utm_campaign,
          utmContent: utm.utm_content,
          utmTerm: utm.utm_term,
          gclid: utm.gclid,
          fbclid: utm.fbclid,
          msclkid: utm.msclkid,
          landingPage: utm.landing_page
        })
      });
      if (!res.ok) throw new Error('Failed to send');
      trackLeadSubmitted({
        leadType: 'property_inquiry',
        propertyMlsId: listing.mls_number || undefined,
        propertyAddress: listing.address || `Property ${listing.mls_number}`,
        propertyPrice: listing.list_price || undefined,
        email: btmEmail,
        phone: btmPhone || undefined
      });
      setBtmSubmitted(true);
    } catch {
      setBtmError('Something went wrong. Please try again.');
    } finally {
      setBtmSubmitting(false);
    }
  };
  const handleFormSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (!formFirstName || !formLastName || !formEmail) return;
    setFormSubmitting(true);
    setFormError('');
    try {
      const utm_0 = getUTMData();
      const recaptchaToken_0 = await getRecaptchaToken('submit_lead');
      const res_0 = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recaptchaToken: recaptchaToken_0,
          firstName: formFirstName,
          lastName: formLastName,
          email: formEmail,
          phone: formPhone || undefined,
          message: formMessage || undefined,
          leadType: 'property_inquiry',
          propertyAddress: listing.address || `Property ${listing.mls_number}`,
          propertyMlsId: listing.mls_number || undefined,
          propertyPrice: listing.list_price || undefined,
          source: 'Property Detail Page',
          sourceUrl: utm_0.source_url,
          referrer: utm_0.referrer,
          utmSource: utm_0.utm_source,
          utmMedium: utm_0.utm_medium,
          utmCampaign: utm_0.utm_campaign,
          utmContent: utm_0.utm_content,
          utmTerm: utm_0.utm_term,
          gclid: utm_0.gclid,
          fbclid: utm_0.fbclid,
          msclkid: utm_0.msclkid,
          landingPage: utm_0.landing_page
        })
      });
      if (!res_0.ok) throw new Error('Failed to send');
      trackLeadSubmitted({
        leadType: 'property_inquiry',
        propertyMlsId: listing.mls_number || undefined,
        propertyAddress: listing.address || `Property ${listing.mls_number}`,
        propertyPrice: listing.list_price || undefined,
        email: formEmail,
        phone: formPhone || undefined
      });
      setFormSubmitted(true);
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };
  const photos = listing.photos || [];
  const handlePrevPhoto = useCallback(() => {
    setActivePhotoIndex(prev => prev === 0 ? photos.length - 1 : prev - 1);
  }, [photos.length]);
  const handleNextPhoto = useCallback(() => {
    setActivePhotoIndex(prev_0 => prev_0 === photos.length - 1 ? 0 : prev_0 + 1);
  }, [photos.length]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openLightbox = useCallback((index?: number) => {
    if (index !== undefined) setActivePhotoIndex(index);
    setLightboxOpen(true);
  }, []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e_1: KeyboardEvent) => {
      if (e_1.key === 'Escape') closeLightbox();
      if (e_1.key === 'ArrowLeft') handlePrevPhoto();
      if (e_1.key === 'ArrowRight') handleNextPhoto();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, closeLightbox, handlePrevPhoto, handleNextPhoto]);

  // Truncate description for "Read More"
  // Normalize hard-wrapped MLS text into clean paragraphs (blank-line separated)
  // so whitespace-pre-wrap no longer renders every soft-wrap as its own line.
  const description = toDescriptionParagraphs(listing.description).join('\n\n');
  const shouldTruncate = description.length > 600;
  const displayDescription = shouldTruncate && !showFullDescription ? description.slice(0, 600) + '...' : description;
  return (
    // Always pull the root up under the transparent nav. Exclusive listings'
    // image hero fills the 80px band; the non-exclusive fallback paints its
    // own solid blue band there so the nav doesn't sit on white.
    <div className="min-h-screen bg-[var(--rc-cream)] -mt-20">
      {!agent && <div className="h-20 bg-[var(--color-sothebys-blue)]" />}
      {/* Hero Image with Navigation — exclusive listings only */}
      {agent && <div className="relative w-full h-screen">
          {/* Photo Slideshow */}
          {photos.length > 0 ? <>
              {photos.map((photo, index_0) => <div key={index_0} className={`absolute inset-0 transition-opacity duration-700 ${index_0 === activePhotoIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                  {failedPhotos.has(index_0) ? <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <div className="text-center text-gray-400">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                    </div> : <Image src={photo} alt={`${listing.address || 'Property'} - Photo ${index_0 + 1}`} fill className="object-cover" sizes="100vw" priority={index_0 === 0} onError={() => setFailedPhotos(prev_1 => new Set(prev_1).add(index_0))} />}
                </div>)}

              {/* Photo Navigation Arrows */}
              {photos.length > 1 && <>
                  <button onClick={handlePrevPhoto} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-20" aria-label="Previous photo">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button onClick={handleNextPhoto} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-20" aria-label="Next photo">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Photo counter + fullscreen */}
                  <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                    <div className="bg-black/50 text-white text-xs px-3 py-1.5 tracking-wider">
                      {activePhotoIndex + 1} / {photos.length}
                    </div>
                    <button onClick={() => openLightbox()} className="bg-black/50 text-white p-1.5 hover:bg-black/70 transition-colors" aria-label="View fullscreen gallery">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </button>
                  </div>
                </>}
            </> : <div className="w-full h-full bg-[var(--rc-navy)]/10 flex items-center justify-center">
              <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>}

          {/* Property Info Bar — on hero */}
          <div className="absolute bottom-[124px] md:bottom-[132px] left-4 md:left-8 lg:left-12 z-20 bg-white/95 backdrop-blur-sm shadow-lg border-t-[3px] border-[var(--rc-gold)]">
            <div className="flex items-center">
              <div className="px-5 py-3 md:px-6 md:py-4">
                <h1 className="text-[var(--rc-navy)] font-bold uppercase tracking-[0.1em] line-clamp-1" style={{
                fontFamily: 'var(--font-figtree), Figtree, sans-serif',
                fontSize: '1rem'
              }}>
                  {listing.address?.split(',')[0] || listing.address}
                </h1>
                <div className="text-[var(--rc-brown)]/70 text-xs uppercase tracking-[0.1em] mt-0.5">
                  {listing.city}{listing.state ? `, ${listing.state}` : ''} {listing.zip_code}
                </div>
                <div className="text-[var(--rc-navy)] text-base md:text-lg font-bold mt-1">
                  {isSold ? `Sold ${displayPrice}` : displayPrice}
                </div>
                {soldDate && <div className="text-[var(--rc-brown)]/70 text-xs mt-0.5">
                    Sold {soldDate}
                  </div>}
              </div>
              <div className="w-[2px] self-stretch bg-[var(--rc-gold)]" />
              <div className="px-5 py-3 md:px-6 md:py-4 grid grid-cols-2 gap-x-5 gap-y-1.5 text-[var(--rc-brown)] text-xs">
                {listing.bedrooms !== null && <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <span>{listing.bedrooms}</span>
                  </div>}
                {listing.bathrooms !== null && <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{listing.bathrooms}</span>
                  </div>}
                {listing.square_feet && <div className="flex items-center gap-1.5 col-span-2">
                    <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    <span>{listing.square_feet.toLocaleString()} SQ FT</span>
                  </div>}
              </div>
              <div className="pr-4 md:pr-5">
                <SavePropertyButton listingId={listing.id} listingType="mls" variant="icon" />
              </div>
            </div>
          </div>
        </div>}

      {/* Spacer — exclusive only */}
      {agent && <div className="h-[50px] bg-[var(--rc-cream)]" />}

      {/* Breadcrumbs — non-exclusive only */}
      {!agent && <div className="bg-[var(--rc-navy)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-3">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase">
                <li>
                  <Link href="/" className="text-white/50 hover:text-white/80 transition-colors">Home</Link>
                </li>
                <li className="text-white/30">/</li>
                <li>
                  <Link href="/real-estate-for-sale" className="text-white/50 hover:text-white/80 transition-colors">Listings</Link>
                </li>
                <li className="text-white/30">/</li>
                <li className="text-white/80">{listing.address?.split(',')[0] || listing.mls_number}</li>
              </ol>
            </nav>
          </div>
        </div>}

      {/* Two-Column Content */}
      <div className={`max-w-[1400px] mx-auto px-6 md:px-8 pb-16 md:pb-24 ${!agent ? 'pt-7' : ''}`}>
        {/* Property Info Bar — full width, non-exclusive only */}
        {!agent && <div className="border-b border-[var(--rc-brown)]/10 mb-4 py-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-[var(--rc-navy)] tracking-wide">
                  {listing.address?.split(',')[0] || listing.address}
                </h2>
                <p className="text-[var(--rc-brown)]/70 text-sm mt-1 tracking-wide">
                  {listing.city}{listing.state ? `, ${listing.state}` : ''} {listing.zip_code}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-6 md:gap-8">
                {listing.bedrooms !== null && <div className="text-center">
                    <p className="text-lg font-light text-[var(--rc-navy)]">{listing.bedrooms}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--rc-brown)]/50">Beds</p>
                  </div>}
                {listing.bathrooms !== null && <div className="text-center">
                    <p className="text-lg font-light text-[var(--rc-navy)]">{listing.bathrooms}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--rc-brown)]/50">Baths</p>
                  </div>}
                {listing.square_feet && <div className="text-center">
                    <p className="text-lg font-light text-[var(--rc-navy)]">{listing.square_feet.toLocaleString()}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--rc-brown)]/50">Sq Ft</p>
                  </div>}
                <div className="h-10 w-px bg-[var(--rc-brown)]/15 hidden md:block" />
                <div>
                  {isSold && <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--rc-brown)]/50">
                      Sold Price
                    </p>}
                  <p className="font-serif text-2xl md:text-3xl text-[var(--rc-navy)]">
                    {displayPrice}
                  </p>
                  {soldDate && <p className="text-[var(--rc-brown)]/70 text-xs mt-0.5">
                      Sold {soldDate}
                    </p>}
                </div>
                <SavePropertyButton listingId={listing.id} listingType="mls" variant="icon" />
              </div>
            </div>
          </div>}

        <div className={`grid grid-cols-1 ${agent ? 'lg:grid-cols-2' : 'lg:grid-cols-[60%_1fr]'} gap-8 lg:gap-10 overflow-hidden`}>
          {/* Left Column: Property Details */}
          <div className="min-w-0 overflow-hidden">
            {/* Compact Gallery — non-exclusive listings only */}
            {!agent && <div className="mb-8">
              <div className="relative aspect-[4/3] overflow-hidden">
                {photos.length > 0 ? <>
                    {photos.map((photo_0, index_1) => <div key={index_1} className={`absolute inset-0 transition-opacity duration-700 ${index_1 === activePhotoIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                        {failedPhotos.has(index_1) ? <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                          </div> : <Image src={photo_0} alt={`${listing.address || 'Property'} - Photo ${index_1 + 1}`} fill className="object-cover" sizes="900px" priority={index_1 === 0} onError={() => setFailedPhotos(prev_2 => new Set(prev_2).add(index_1))} />}
                      </div>)}

                    {photos.length > 1 && <>
                        <button onClick={handlePrevPhoto} className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-20" aria-label="Previous photo">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button onClick={handleNextPhoto} className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-20" aria-label="Next photo">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                          <div className="bg-black/50 text-white text-xs px-3 py-1.5 tracking-wider">
                            {activePhotoIndex + 1} / {photos.length}
                          </div>
                          <button onClick={() => openLightbox()} className="bg-black/50 text-white p-1.5 hover:bg-black/70 transition-colors" aria-label="View fullscreen gallery">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </>}
                  </> : <div className="absolute inset-0 bg-[var(--rc-navy)]/10 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>}
              </div>

              {/* Thumbnails */}
              {photos.length > 1 && <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                  {photos.map((photo_1, index_2) => <button key={index_2} onClick={() => setActivePhotoIndex(index_2)} className={`relative flex-shrink-0 w-16 h-12 md:w-20 md:h-14 overflow-hidden transition-all duration-200 ${index_2 === activePhotoIndex ? 'ring-2 ring-[var(--rc-gold)] opacity-100' : 'opacity-50 hover:opacity-80'}`} aria-label={`View photo ${index_2 + 1}`}>
                      <Image src={photo_1} alt={`Thumbnail ${index_2 + 1}`} fill className="object-cover" sizes="80px" />
                    </button>)}
                </div>}
              </div>}

            {/* Heading */}
            <h3 className="text-[var(--rc-navy)] text-lg md:text-xl font-bold uppercase tracking-[0.1em] mb-4" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif'
            }}>
              {listing.address?.split(',')[0] || 'Property Details'}
            </h3>

            {/* Gold divider */}
            <div className="w-full h-[2px] bg-[var(--rc-gold)] mb-6" />

            {/* Subheading */}
            <p className="text-[var(--rc-gold)] text-base font-normal mb-6" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif'
            }}>
              {listing.city}{listing.state ? `, ${listing.state}` : ''} {listing.zip_code}
            </p>

            {/* Description */}
            {description && <div className="mb-8">
                <p className="text-[var(--rc-brown)] text-base leading-[1.85] whitespace-pre-wrap">
                  {displayDescription}
                </p>
                {shouldTruncate && <button onClick={() => setShowFullDescription(!showFullDescription)} className="mt-4 text-[var(--rc-navy)] text-sm font-bold uppercase tracking-[0.1em] flex items-center gap-2 hover:text-[var(--rc-gold)] transition-colors">
                    {showFullDescription ? 'Read Less' : 'Read More'}
                    <svg className={`w-3 h-3 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>}
              </div>}

            {/* Property Details Grid */}
            <div className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                  <span className="text-[var(--rc-brown)]/60 text-sm">MLS #</span>
                  <span className="text-[var(--rc-navy)] text-sm font-medium">{listing.mls_number}</span>
                </div>
                {listing.property_type && <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                    <span className="text-[var(--rc-brown)]/60 text-sm">Type</span>
                    <span className="text-[var(--rc-navy)] text-sm font-medium">{listing.property_type}</span>
                  </div>}
                {listing.year_built && <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                    <span className="text-[var(--rc-brown)]/60 text-sm">Year Built</span>
                    <span className="text-[var(--rc-navy)] text-sm font-medium">{listing.year_built}</span>
                  </div>}
                {listing.lot_size && <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                    <span className="text-[var(--rc-brown)]/60 text-sm">Lot Size</span>
                    <span className="text-[var(--rc-navy)] text-sm font-medium">
                      {`${listing.lot_size.toFixed(2)} Acres`}
                    </span>
                  </div>}
                {listing.subdivision_name && <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                    <span className="text-[var(--rc-brown)]/60 text-sm">Subdivision</span>
                    <span className="text-[var(--rc-navy)] text-sm font-medium">{listing.subdivision_name}</span>
                  </div>}
                {listing.days_on_market !== null && listing.days_on_market !== undefined && <div className="flex justify-between items-baseline py-2.5 border-b border-[var(--rc-brown)]/10">
                    <span className="text-[var(--rc-brown)]/60 text-sm">Days on Market</span>
                    <span className="text-[var(--rc-navy)] text-sm font-medium">{listing.days_on_market}</span>
                  </div>}
              </div>
            </div>

          </div>

          {/* Right Column: Contact Form + Share + Agent */}
          <div className="min-w-0 space-y-8">
            {/* Listing Agent card — non-exclusive listings only, shows Chris Klug as the brokerage contact */}
            {!agent && defaultAgent && <div className="bg-white border border-[var(--rc-brown)]/10 p-6">
                <div className="flex items-start gap-4">
                  {defaultAgent.imageUrl && <div className="relative w-32 h-[147px] flex-shrink-0 overflow-hidden">
                      <Image src={defaultAgent.imageUrl} alt={defaultAgent.name} fill className="object-cover object-top" sizes="128px" />
                    </div>}
                  <div>
                    <Link href={`/real-estate-agent/${defaultAgent.slug.current}`} className="font-serif text-lg text-[var(--rc-navy)] hover:text-[var(--rc-gold)] transition-colors">
                      {defaultAgent.name}
                    </Link>
                    {defaultAgent.title && <p className="text-xs text-[var(--rc-brown)]/60 mt-0.5">{defaultAgent.title}</p>}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {defaultAgent.phone && <a href={`tel:${phoneHref(defaultAgent.phone)}`} className="flex items-center gap-2 text-[var(--rc-brown)] hover:text-[var(--rc-gold)] transition-colors">
                      <svg className="w-4 h-4 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {formatPhone(defaultAgent.phone)}
                    </a>}
                  {defaultAgent.email && <a href={`mailto:${defaultAgent.email}`} className="flex items-center gap-2 text-[var(--rc-brown)] hover:text-[var(--rc-gold)] transition-colors">
                      <svg className="w-4 h-4 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {defaultAgent.email}
                    </a>}
                </div>
              </div>}

            {/* Inquire About This Property — matches the exclusive listing template */}
            <div className="bg-white border border-[var(--rc-brown)]/10 p-6">
              <h3 className="font-serif text-lg text-[var(--rc-navy)] mb-1">Inquire About This Property</h3>
              <div className="w-8 h-[2px] bg-[var(--rc-gold)] mb-5" />

              {formSubmitted ? <div className="text-center py-8">
                  <svg className="w-10 h-10 text-[var(--rc-gold)] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-[var(--rc-navy)] font-medium">Thank you for your inquiry.</p>
                  <p className="text-[var(--rc-brown)]/60 text-sm mt-1">We will be in touch shortly.</p>
                </div> : <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="req-firstName" className="text-[10px] tracking-[0.15em] text-[var(--rc-brown)]/60 uppercase block mb-1.5">First Name</label>
                      <input id="req-firstName" type="text" required value={formFirstName} onChange={e_2 => setFormFirstName(e_2.target.value)} className="w-full px-4 py-3 bg-white border border-[var(--rc-brown)]/20 text-[var(--rc-navy)] placeholder:text-[var(--rc-brown)]/40 focus:border-[var(--rc-gold)] focus:outline-none transition-colors text-sm" placeholder="First" />
                    </div>
                    <div>
                      <label htmlFor="req-lastName" className="text-[10px] tracking-[0.15em] text-[var(--rc-brown)]/60 uppercase block mb-1.5">Last Name</label>
                      <input id="req-lastName" type="text" required value={formLastName} onChange={e_3 => setFormLastName(e_3.target.value)} className="w-full px-4 py-3 bg-white border border-[var(--rc-brown)]/20 text-[var(--rc-navy)] placeholder:text-[var(--rc-brown)]/40 focus:border-[var(--rc-gold)] focus:outline-none transition-colors text-sm" placeholder="Last" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="req-email" className="text-[10px] tracking-[0.15em] text-[var(--rc-brown)]/60 uppercase block mb-1.5">Email</label>
                    <input id="req-email" type="email" required value={formEmail} onChange={e_4 => setFormEmail(e_4.target.value)} className="w-full px-4 py-3 bg-white border border-[var(--rc-brown)]/20 text-[var(--rc-navy)] placeholder:text-[var(--rc-brown)]/40 focus:border-[var(--rc-gold)] focus:outline-none transition-colors text-sm" placeholder="Email address" />
                  </div>
                  <div>
                    <label htmlFor="req-phone" className="text-[10px] tracking-[0.15em] text-[var(--rc-brown)]/60 uppercase block mb-1.5">Phone</label>
                    <input id="req-phone" type="tel" value={formPhone} onChange={e_5 => setFormPhone(e_5.target.value)} className="w-full px-4 py-3 bg-white border border-[var(--rc-brown)]/20 text-[var(--rc-navy)] placeholder:text-[var(--rc-brown)]/40 focus:border-[var(--rc-gold)] focus:outline-none transition-colors text-sm" placeholder="Phone number" />
                  </div>
                  <div>
                    <label htmlFor="req-message" className="text-[10px] tracking-[0.15em] text-[var(--rc-brown)]/60 uppercase block mb-1.5">Message</label>
                    <textarea id="req-message" rows={3} value={formMessage} onChange={e_6 => setFormMessage(e_6.target.value)} className="w-full px-4 py-3 bg-white border border-[var(--rc-brown)]/20 text-[var(--rc-navy)] placeholder:text-[var(--rc-brown)]/40 focus:border-[var(--rc-gold)] focus:outline-none transition-colors text-sm resize-none" placeholder={`I'm interested in ${listing.address?.split(',')[0] || 'this property'}...`} />
                  </div>

                  {formError && <p className="text-red-600 text-xs">{formError}</p>}

                  <button type="submit" disabled={formSubmitting || !formFirstName || !formLastName || !formEmail} className="sir-btn w-full py-3 text-xs tracking-[0.15em] uppercase disabled:opacity-50 disabled:cursor-not-allowed">
                    {formSubmitting ? 'Sending...' : 'Send Inquiry'}
                  </button>
                </form>}
            </div>

            {/* Share Property */}
            <div className="pt-4">
              <h4 className="text-sm font-bold uppercase tracking-[0.1em] mb-3" style={{
                fontFamily: 'var(--font-figtree), Figtree, sans-serif',
                color: '#ffffff'
              }}>
                Share Property
              </h4>
              <div className="flex items-center gap-3">
                {/* Twitter/X */}
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}&text=${encodeURIComponent(listing.address || 'Check out this property')}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-[var(--rc-brown)]/20 flex items-center justify-center text-[var(--rc-brown)] hover:border-[var(--rc-gold)] hover:text-[var(--rc-gold)] transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                </a>
                {/* Facebook */}
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-[var(--rc-brown)]/20 flex items-center justify-center text-[var(--rc-brown)] hover:border-[var(--rc-gold)] hover:text-[var(--rc-gold)] transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </a>
                {/* Instagram */}
                <a href="#" className="w-8 h-8 rounded-full border border-[var(--rc-brown)]/20 flex items-center justify-center text-[var(--rc-brown)] hover:border-[var(--rc-gold)] hover:text-[var(--rc-gold)] transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                </a>
                {/* Email */}
                <a href={`mailto:?subject=${encodeURIComponent(listing.address || 'Property')}&body=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} className="w-8 h-8 rounded-full border border-[var(--rc-brown)]/20 flex items-center justify-center text-[var(--rc-brown)] hover:border-[var(--rc-gold)] hover:text-[var(--rc-gold)] transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Agent Cards */}
            {agent && <div className="space-y-6">
                {/* Listing Agent */}
                <div className="flex items-start gap-5">
                  {/* Agent Photo */}
                  {agent.imageUrl ? <Link href={`/agents/${agent.slug.current}`} className="flex-shrink-0">
                      <div className="relative w-28 h-36 overflow-hidden grayscale hover:grayscale-0 transition-all duration-300">
                        <Image src={agent.imageUrl} alt={agent.name} fill className="object-cover" sizes="112px" />
                      </div>
                    </Link> : <div className="w-28 h-36 bg-[var(--rc-navy)]/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-10 h-10 text-[var(--rc-brown)]/30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    </div>}

                  {/* Agent Info */}
                  <div className="min-w-0 pt-2">
                    <Link href={`/agents/${agent.slug.current}`} className="text-[var(--rc-navy)] text-base font-bold uppercase tracking-[0.08em] hover:text-[var(--rc-gold)] transition-colors" style={{
                    fontFamily: 'var(--font-figtree), Figtree, sans-serif'
                  }}>
                      {agent.name}
                    </Link>
                    {agent.title && <p className="text-[var(--rc-brown)] text-sm mt-0.5">{agent.title}</p>}
                    <div className="mt-3 space-y-1.5">
                      {agent.phone && <a href={`tel:${phoneHref(agent.phone)}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                          <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {formatPhone(agent.phone)}
                        </a>}
                      {agent.mobile && agent.mobile !== agent.phone && <a href={`tel:${phoneHref(agent.mobile)}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                          <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          {formatPhone(agent.mobile)}
                        </a>}
                      {agent.email && <a href={`mailto:${agent.email}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                          <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {agent.email}
                        </a>}
                    </div>
                  </div>
                </div>

                {/* Co-Listing Agent */}
                {coAgent && <div>
                    <p className="text-[var(--rc-brown)]/60 text-xs uppercase tracking-[0.15em] mb-3">Co-Listing Agent</p>
                    <div className="flex items-start gap-5">
                      {coAgent.imageUrl ? <Link href={`/agents/${coAgent.slug.current}`} className="flex-shrink-0">
                          <div className="relative w-28 h-36 overflow-hidden grayscale hover:grayscale-0 transition-all duration-300">
                            <Image src={coAgent.imageUrl} alt={coAgent.name} fill className="object-cover" sizes="112px" />
                          </div>
                        </Link> : <div className="w-28 h-36 bg-[var(--rc-navy)]/10 flex items-center justify-center flex-shrink-0">
                          <svg className="w-10 h-10 text-[var(--rc-brown)]/30" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </div>}
                      <div className="min-w-0 pt-2">
                        <Link href={`/agents/${coAgent.slug.current}`} className="text-[var(--rc-navy)] text-base font-bold uppercase tracking-[0.08em] hover:text-[var(--rc-gold)] transition-colors" style={{
                      fontFamily: 'var(--font-figtree), Figtree, sans-serif'
                    }}>
                          {coAgent.name}
                        </Link>
                        {coAgent.title && <p className="text-[var(--rc-brown)] text-sm mt-0.5">{coAgent.title}</p>}
                        <div className="mt-3 space-y-1.5">
                          {coAgent.phone && <a href={`tel:${phoneHref(coAgent.phone)}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                              <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              {formatPhone(coAgent.phone)}
                            </a>}
                          {coAgent.mobile && coAgent.mobile !== coAgent.phone && <a href={`tel:${phoneHref(coAgent.mobile)}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                              <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              {formatPhone(coAgent.mobile)}
                            </a>}
                          {coAgent.email && <a href={`mailto:${coAgent.email}`} className="flex items-center gap-2 text-[var(--rc-brown)] text-sm hover:text-[var(--rc-gold)] transition-colors">
                              <svg className="w-3.5 h-3.5 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {coAgent.email}
                            </a>}
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>}
          </div>
        </div>
      </div>

      {/* DETAILS Section — Navy Background */}
      <section className="bg-[var(--rc-navy)] py-16 md:py-24">
        {/* Details Title */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light uppercase tracking-[0.08em]" style={{
            fontFamily: 'var(--font-figtree), Figtree, sans-serif',
            lineHeight: '1.1em',
            color: '#ffffff'
          }}>
            Details
          </h2>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-8 space-y-10">
          {/* Listing Overview — moved to top */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif',
              color: '#ffffff'
            }}>
              Listing Overview
            </h3>
            <div className="w-full h-[1px] bg-[var(--rc-gold)]/40 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
              <div>
                <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Listing Price</div>
                <div className="text-white text-sm">{formatPrice(listing.list_price)}</div>
              </div>
              {isSold && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Sold Price</div>
                  <div className="text-white text-sm">{displayPrice}</div>
                </div>}
              {soldDate && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Sold Date</div>
                  <div className="text-white text-sm">{soldDate}</div>
                </div>}
              <div>
                <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">MLS #</div>
                <div className="text-white text-sm">{listing.mls_number}</div>
              </div>
              {listing.status && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Status</div>
                  <div className="text-white text-sm capitalize">{listing.status}</div>
                </div>}
              {listing.listing_date && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Listed</div>
                  <div className="text-white text-sm">
                    {new Date(listing.listing_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  </div>
                </div>}
              {listing.days_on_market !== null && listing.days_on_market !== undefined && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Days on Market</div>
                  <div className="text-white text-sm">{listing.days_on_market}</div>
                </div>}
              {listing.bathrooms_full !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Full Baths</div>
                  <div className="text-white text-sm">{listing.bathrooms_full}</div>
                </div>}
              {listing.bathrooms_half !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Half Baths</div>
                  <div className="text-white text-sm">{listing.bathrooms_half}</div>
                </div>}
              {listing.bathrooms_three_quarter !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">3/4 Baths</div>
                  <div className="text-white text-sm">{listing.bathrooms_three_quarter}</div>
                </div>}
            </div>
          </div>

          {/* Essential Info */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif',
              color: '#ffffff'
            }}>
              Essential Info
            </h3>
            <div className="w-full h-[1px] bg-[var(--rc-gold)]/40 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
              {listing.lot_size !== null && listing.lot_size !== undefined && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Lot Size</div>
                  <div className="text-white text-sm">
                    {`${listing.lot_size.toFixed(2)} Acres`}
                  </div>
                </div>}
              {listing.bedrooms !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Bedrooms</div>
                  <div className="text-white text-sm">{listing.bedrooms}</div>
                </div>}
              {listing.bathrooms !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Bathrooms</div>
                  <div className="text-white text-sm">{listing.bathrooms}</div>
                </div>}
              {listing.square_feet !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Sq. Ft.</div>
                  <div className="text-white text-sm">{listing.square_feet.toLocaleString()}</div>
                </div>}
              {listing.property_type && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Property Type</div>
                  <div className="text-white text-sm">{listing.property_type}</div>
                </div>}
              {listing.year_built !== null && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Built</div>
                  <div className="text-white text-sm">{listing.year_built}</div>
                </div>}
              {listing.subdivision_name && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Subdivision</div>
                  <div className="text-white text-sm">{listing.subdivision_name}</div>
                </div>}
              {listing.mls_area_minor && <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Area</div>
                  <div className="text-white text-sm">{listing.mls_area_minor}</div>
                </div>}
            </div>
          </div>

          {/* Features */}
          {(listing.cooling?.length || listing.heating?.length || listing.parking_features?.length || listing.laundry_features?.length || listing.fireplace_features?.length || listing.furnished) && <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif',
              color: '#ffffff'
            }}>
                Features
              </h3>
              <div className="w-full h-[1px] bg-[var(--rc-gold)]/40 mb-8" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
                {listing.cooling && listing.cooling.length > 0 && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Cooling</div>
                    <div className="text-white text-sm">{listing.cooling.join(', ')}</div>
                  </div>}
                {listing.heating && listing.heating.length > 0 && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Heating</div>
                    <div className="text-white text-sm">{listing.heating.join(', ')}</div>
                  </div>}
                {listing.parking_features && listing.parking_features.length > 0 && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Parking</div>
                    <div className="text-white text-sm">{listing.parking_features.join(', ')}</div>
                  </div>}
                {listing.laundry_features && listing.laundry_features.length > 0 && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Laundry</div>
                    <div className="text-white text-sm">{listing.laundry_features.join(', ')}</div>
                  </div>}
                {listing.fireplace_features && listing.fireplace_features.length > 0 && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Fireplace</div>
                    <div className="text-white text-sm">{listing.fireplace_features.join(', ')}</div>
                  </div>}
                {listing.fireplace_total !== null && listing.fireplace_total !== undefined && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Fireplaces</div>
                    <div className="text-white text-sm">{listing.fireplace_total}</div>
                  </div>}
                {listing.furnished && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Furnished</div>
                    <div className="text-white text-sm">{listing.furnished}</div>
                  </div>}
                {listing.attached_garage_yn !== null && listing.attached_garage_yn !== undefined && <div>
                    <div className="text-white/50 text-xs uppercase tracking-[0.1em] mb-1">Attached Garage</div>
                    <div className="text-white text-sm">{listing.attached_garage_yn ? 'Yes' : 'No'}</div>
                  </div>}
              </div>
            </div>}

          {/* Amenities */}
          {listing.association_amenities && listing.association_amenities.length > 0 && <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] mb-3" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif',
              color: '#ffffff'
            }}>
                Amenities
              </h3>
              <div className="w-full h-[1px] bg-[var(--rc-gold)]/40 mb-8" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3">
                {listing.association_amenities.map((amenity, index_3) => <div key={index_3} className="text-white text-sm flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[var(--rc-gold)] rotate-45 flex-shrink-0" />
                    {amenity}
                  </div>)}
              </div>
            </div>}

        </div>
      </section>

      {/* Media Section — Gallery / Video / Virtual Tour */}
      <MediaSection listing={listing} isExclusive={!!agent} />

      {/* Map Section */}
      {listing.latitude && listing.longitude && <section className="bg-gray-100 border-t border-[var(--rc-brown)]/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-8 pt-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[var(--rc-navy)] text-sm font-bold uppercase tracking-[0.15em]" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif'
            }}>
                Location
              </h2>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`} target="_blank" rel="noopener noreferrer" className="text-[var(--rc-gold)] text-sm hover:underline flex items-center gap-1">
                Get Directions
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
          <div className="h-[500px] w-full">
            <PropertyMap latitude={listing.latitude} longitude={listing.longitude} address={listing.address || undefined} price={listing.list_price} googleMapsApiKey={googleMapsApiKey} />
          </div>
        </section>}

      {/* Bottom Contact Form */}
      <section className="bg-[var(--rc-navy)] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light uppercase tracking-[0.08em] mb-4" style={{
              fontFamily: 'var(--font-figtree), Figtree, sans-serif',
              lineHeight: '1.1em',
              color: '#ffffff'
            }}>
              Inquire About This Property
            </h2>
            <p className="text-white/60 text-base max-w-lg mx-auto">
              Interested in {listing.address?.split(',')[0] || 'this property'}? Fill out the form below and an agent will be in touch.
            </p>
          </div>

          {btmSubmitted ? <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-[var(--rc-gold)]/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-[var(--rc-gold)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-white text-lg font-medium mb-1">Thank You!</p>
              <p className="text-white/60 text-sm">Your inquiry has been sent. An agent will respond within 24 hours.</p>
            </div> : <form className="space-y-5" onSubmit={handleBtmFormSubmit}>
              {btmError && <p className="text-red-400 text-sm text-center">{btmError}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input type="text" placeholder="First name" value={btmFirstName} onChange={e_7 => setBtmFirstName(e_7.target.value)} required className="w-full bg-transparent border-0 border-b border-white/30 text-white text-sm py-3 px-0 placeholder:text-white/40 focus:border-[var(--rc-gold)] focus:ring-0 outline-none transition-colors" />
                <input type="text" placeholder="Last name" value={btmLastName} onChange={e_8 => setBtmLastName(e_8.target.value)} required className="w-full bg-transparent border-0 border-b border-white/30 text-white text-sm py-3 px-0 placeholder:text-white/40 focus:border-[var(--rc-gold)] focus:ring-0 outline-none transition-colors" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input type="email" placeholder="Email" value={btmEmail} onChange={e_9 => setBtmEmail(e_9.target.value)} required className="w-full bg-transparent border-0 border-b border-white/30 text-white text-sm py-3 px-0 placeholder:text-white/40 focus:border-[var(--rc-gold)] focus:ring-0 outline-none transition-colors" />
                <input type="tel" placeholder="Phone" value={btmPhone} onChange={e_10 => setBtmPhone(e_10.target.value)} className="w-full bg-transparent border-0 border-b border-white/30 text-white text-sm py-3 px-0 placeholder:text-white/40 focus:border-[var(--rc-gold)] focus:ring-0 outline-none transition-colors" />
              </div>
              <textarea placeholder="Message..." rows={4} value={btmMessage} onChange={e_11 => setBtmMessage(e_11.target.value)} className="w-full bg-transparent border-0 border-b border-white/30 text-white text-sm py-3 px-0 placeholder:text-white/40 focus:border-[var(--rc-gold)] focus:ring-0 outline-none transition-colors resize-none" />
              <div className="pt-2 text-center">
                <button type="submit" disabled={btmSubmitting || !btmFirstName || !btmLastName || !btmEmail} className="bg-transparent border border-white/30 text-white text-[11px] font-bold uppercase tracking-[0.15em] px-12 py-3.5 hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                  {btmSubmitting ? 'Sending...' : 'Send Inquiry'}
                </button>
              </div>
            </form>}
        </div>
      </section>

      {/* Listing attribution + MLS disclaimer — sits below the inquire form */}
      <div className="bg-[var(--rc-cream)] py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8">
          {/* Listing attribution — agent + office, with optional contact phone */}
          {!agent && (listing.list_agent_full_name || listing.list_office_name) && <p className="text-[var(--rc-brown)] text-xs mb-3">
              {listing.list_agent_full_name && listing.list_office_name ? <>Listed by {listing.list_agent_full_name} with {listing.list_office_name}</> : <>Listed with {listing.list_agent_full_name || listing.list_office_name}</>}
              {listing.list_office_phone && <>, Listing Contact: {listing.list_office_phone}</>}
            </p>}

          <p className="text-[var(--rc-brown)]/40 text-xs leading-relaxed">
            MLS# {listing.mls_number} — Listing information is deemed reliable but not guaranteed. All measurements and square footage are approximate.
          </p>
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && photos.length > 0 && <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
            <div className="text-white/60 text-sm tracking-wider">
              {activePhotoIndex + 1} / {photos.length}
            </div>
            <div className="text-white text-sm tracking-[0.1em] uppercase hidden md:block">
              {listing.address?.split(',')[0]}
            </div>
            <button onClick={closeLightbox} className="text-white/70 hover:text-white transition-colors p-1" aria-label="Close gallery">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main image area */}
          <div className="flex-1 flex items-center justify-center relative min-h-0 px-4 md:px-16">
            {/* Prev */}
            <button onClick={handlePrevPhoto} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-10" aria-label="Previous photo">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="relative w-full h-full max-w-6xl mx-auto">
              {failedPhotos.has(activePhotoIndex) ? <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <p className="mt-4">Photo unavailable</p>
                  </div>
                </div> : <Image src={photos[activePhotoIndex]} alt={`${listing.address || 'Property'} - Photo ${activePhotoIndex + 1}`} fill className="object-contain" sizes="100vw" priority onError={() => setFailedPhotos(prev_3 => new Set(prev_3).add(activePhotoIndex))} />}
            </div>

            {/* Next */}
            <button onClick={handleNextPhoto} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-10" aria-label="Next photo">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="flex-shrink-0 px-6 py-4">
            <div className="flex gap-2 justify-center overflow-x-auto">
              {photos.map((photo_2, index_4) => <button key={index_4} onClick={() => setActivePhotoIndex(index_4)} className={`relative flex-shrink-0 w-14 h-10 md:w-16 md:h-12 overflow-hidden transition-all duration-200 ${index_4 === activePhotoIndex ? 'ring-2 ring-[var(--rc-gold)] opacity-100' : 'opacity-40 hover:opacity-70'}`}>
                  <Image src={photo_2} alt={`Thumbnail ${index_4 + 1}`} fill className="object-cover" sizes="64px" />
                </button>)}
            </div>
          </div>
        </div>}
    </div>
  );
}

// Tabbed media section: Gallery, Video, Virtual Tour
function MediaSection(t0) {
  const $ = _c(29);
  const {
    listing,
    isExclusive
  } = t0;
  const hasPhotos = listing.photos && listing.photos.length > 0;
  const hasVirtualTour = !!listing.virtual_tour_url;
  const hasVideo = listing.video_urls && listing.video_urls.length > 0;
  const showGallery = isExclusive && hasPhotos;
  const tabCount = (showGallery ? 1 : 0) + (hasVideo ? 1 : 0) + (hasVirtualTour ? 1 : 0);
  const defaultTab = showGallery ? "gallery" : hasVideo ? "video" : "tour";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [galleryIndex, setGalleryIndex] = useState(0);
  if (tabCount === 0) {
    return null;
  }
  let t1;
  if ($[0] !== listing.photos) {
    t1 = listing.photos || [];
    $[0] = listing.photos;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const photos = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="text-3xl md:text-4xl lg:text-5xl font-light uppercase tracking-[0.08em] mb-8" style={{
      fontFamily: "var(--font-figtree), Figtree, sans-serif",
      lineHeight: "1.1em",
      color: "#c19b5f"
    }}>Media</h2>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== activeTab || $[4] !== hasVideo || $[5] !== hasVirtualTour || $[6] !== showGallery || $[7] !== tabCount) {
    t3 = tabCount > 1 && <div className="flex items-center justify-center gap-1">{showGallery && <button onClick={() => setActiveTab("gallery")} className={`px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${activeTab === "gallery" ? "bg-[var(--rc-gold)] text-white" : "bg-transparent border border-white/30 text-white/70 hover:text-white hover:border-white/60"}`} style={{
        fontFamily: "var(--font-figtree), Figtree, sans-serif"
      }}><span className="flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>Gallery</span></button>}{hasVideo && <button onClick={() => setActiveTab("video")} className={`px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${activeTab === "video" ? "bg-[var(--rc-gold)] text-white" : "bg-transparent border border-white/30 text-white/70 hover:text-white hover:border-white/60"}`} style={{
        fontFamily: "var(--font-figtree), Figtree, sans-serif"
      }}><span className="flex items-center gap-2"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>Video</span></button>}{hasVirtualTour && <button onClick={() => setActiveTab("tour")} className={`px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${activeTab === "tour" ? "bg-[var(--rc-gold)] text-white" : "bg-transparent border border-white/30 text-white/70 hover:text-white hover:border-white/60"}`} style={{
        fontFamily: "var(--font-figtree), Figtree, sans-serif"
      }}><span className="flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Virtual Tour</span></button>}</div>;
    $[3] = activeTab;
    $[4] = hasVideo;
    $[5] = hasVirtualTour;
    $[6] = showGallery;
    $[7] = tabCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="text-center mb-10 md:mb-14">{t2}{t3}</div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== activeTab || $[12] !== galleryIndex || $[13] !== listing.address || $[14] !== photos) {
    t5 = activeTab === "gallery" && photos.length > 0 && <div><div className="relative aspect-video w-full overflow-hidden bg-black"><Image src={photos[galleryIndex]} alt={`${listing.address || "Property"} - Photo ${galleryIndex + 1}`} fill={true} className="object-cover" sizes="1400px" />{photos.length > 1 && <><button onClick={() => setGalleryIndex(prev => prev === 0 ? photos.length - 1 : prev - 1)} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-10" aria-label="Previous photo"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" /></svg></button><button onClick={() => setGalleryIndex(prev_0 => prev_0 === photos.length - 1 ? 0 : prev_0 + 1)} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-[var(--rc-gold)] hover:border-[var(--rc-gold)] transition-all duration-300 z-10" aria-label="Next photo"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" /></svg></button><div className="absolute top-4 right-4 z-10 bg-black/50 text-white text-xs px-3 py-1.5 tracking-wider">{galleryIndex + 1} / {photos.length}</div></>}</div>{photos.length > 1 && <div className="flex gap-2 mt-4 overflow-x-auto pb-2">{photos.map((photo, index) => <button key={index} onClick={() => setGalleryIndex(index)} className={`relative flex-shrink-0 w-20 h-14 overflow-hidden transition-all duration-200 ${index === galleryIndex ? "ring-2 ring-[var(--rc-gold)] opacity-100" : "opacity-40 hover:opacity-70"}`} aria-label={`View photo ${index + 1}`}><Image src={photo} alt={`Thumbnail ${index + 1}`} fill={true} className="object-cover" sizes="80px" /></button>)}</div>}</div>;
    $[11] = activeTab;
    $[12] = galleryIndex;
    $[13] = listing.address;
    $[14] = photos;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== activeTab || $[17] !== listing.video_urls) {
    t6 = activeTab === "video" && listing.video_urls && <div className="space-y-8">{listing.video_urls.map(_temp)}</div>;
    $[16] = activeTab;
    $[17] = listing.video_urls;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== activeTab || $[20] !== listing.virtual_tour_url) {
    t7 = activeTab === "tour" && listing.virtual_tour_url && <div>{isEmbeddableMediaUrl(listing.virtual_tour_url) && <div className="aspect-video w-full"><iframe src={listing.virtual_tour_url} className="w-full h-full border-0" allowFullScreen={true} allow="xr-spatial-tracking" title="Virtual Tour" /></div>}<a href={listing.virtual_tour_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-sm underline underline-offset-4">Open the virtual tour in a new tab</a></div>;
    $[19] = activeTab;
    $[20] = listing.virtual_tour_url;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className="max-w-[1400px] mx-auto px-6 md:px-8">{t5}{t6}{t7}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t4 || $[27] !== t8) {
    t9 = <section className="bg-[var(--rc-navy)] py-16 md:py-24">{t4}{t8}</section>;
    $[26] = t4;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function _temp(url, index_0) {
  return <div key={index_0} className="aspect-video w-full">{url.includes("brightcove") ? <iframe src={url} className="w-full h-full border-0" allowFullScreen={true} allow="encrypted-media" title={`Property Video ${index_0 + 1}`} /> : isEmbeddableMediaUrl(url) ? <video src={url} controls={true} className="w-full h-full bg-black" preload="metadata">Your browser does not support the video tag.</video> : <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full bg-[#f5f5f5] text-sm underline underline-offset-4">Watch property video {index_0 + 1} in a new tab</a>}</div>;
}